import { useEffect, useState } from 'react'
import { Plus, RefreshCw, Save, Trash2, Watch } from 'lucide-react'
import { SectionCard } from '@/components/shared/section-card'
import { normalizeWatchName } from '@/lib/watch-models'

interface WatchModelsEditorProps {
  models: string[]
  saving: boolean
  onSave: (models: string[]) => Promise<boolean>
}

/**
 * Editor daftar nama jam yang dihitung sebagai jam Parlent.
 *
 * Daftar ini dipakai metrik "Users Parlent" di halaman Users. Karena tersimpan
 * di Firestore, menambah model baru tidak perlu rilis ulang aplikasi mobile.
 */
export function WatchModelsEditor({ models, saving, onSave }: WatchModelsEditorProps) {
  const [draft, setDraft] = useState<string[]>(models)
  const [newModel, setNewModel] = useState('')
  const [error, setError] = useState<string | null>(null)

  // Sinkronkan saat data dari Firestore datang/berubah.
  useEffect(() => {
    setDraft(models)
  }, [models])

  function handleAdd() {
    const value = newModel.trim()
    if (!value) {
      setError('Nama model tidak boleh kosong.')
      return
    }
    const isDuplicate = draft.some(
      (item) => normalizeWatchName(item) === normalizeWatchName(value),
    )
    if (isDuplicate) {
      setError(`"${value}" sudah ada di daftar.`)
      return
    }
    setDraft([...draft, value])
    setNewModel('')
    setError(null)
  }

  function handleRemove(index: number) {
    setDraft(draft.filter((_, position) => position !== index))
    setError(null)
  }

  async function handleSave() {
    if (draft.length === 0) {
      setError('Sisakan minimal satu model, jika tidak metrik Users Parlent selalu nol.')
      return
    }
    const success = await onSave(draft)
    setError(success ? null : 'Gagal menyimpan. Coba lagi.')
  }

  return (
    <SectionCard
      title="Model Jam Parlent"
      description="Nama jam yang dihitung sebagai Users Parlent. Pencocokan mengabaikan huruf besar/kecil dan sufiks serial — 'Parlent Active-A3F2' tetap cocok dengan 'Parlent Active'."
      action={
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 rounded-xl bg-orange-500 px-4 py-2 text-sm font-bold text-white transition hover:bg-orange-600 disabled:opacity-50"
        >
          {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {saving ? 'Menyimpan...' : 'Simpan'}
        </button>
      }
    >
      <div className="space-y-3">
        {draft.map((model, index) => (
          <div
            key={`${model}-${index}`}
            className="flex items-center justify-between rounded-xl border border-stone-800 bg-stone-900/60 px-4 py-3"
          >
            <span className="flex items-center gap-2 text-sm font-medium text-stone-200">
              <Watch className="h-4 w-4 text-orange-400" />
              {model}
            </span>
            <button
              onClick={() => handleRemove(index)}
              aria-label={`Hapus ${model}`}
              className="rounded-lg p-2 text-stone-500 transition hover:bg-rose-500/10 hover:text-rose-400"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}

        <div className="flex gap-2">
          <input
            value={newModel}
            onChange={(event) => setNewModel(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') handleAdd()
            }}
            placeholder="Contoh: Parlent Active Pro"
            className="flex-1 rounded-xl border border-stone-700 bg-stone-900 px-4 py-3 text-sm text-stone-200 outline-none transition focus:border-orange-500/50"
          />
          <button
            onClick={handleAdd}
            className="flex items-center gap-2 rounded-xl border border-stone-700 bg-stone-900 px-4 py-3 text-sm font-semibold text-stone-300 transition hover:border-stone-600 hover:text-white"
          >
            <Plus className="h-4 w-4" />
            Tambah
          </button>
        </div>

        {error && <p className="text-sm text-rose-400">{error}</p>}
      </div>
    </SectionCard>
  )
}
